import { useMemo } from "react";
import { usePagesContent } from "../../../../Hooks/usePagesContent";
import {
  SOH_UNAVAILABLE_LABEL,
  splitUnavailableEntry,
  parseRangeBuckets,
  appendUnavailableBucket,
  classifyBucketValue,
  resolveBucketName,
} from "./unavailableBucket";

// ─── Fallbacks used when org meta bucket definitions are absent ───────────────
// Same labels as the backend fixed table (evcell constants.BATTERY_HEALTH_V2_SOH_RANGES),
// so a server StateOfHealthBucket label from that table matches a frontend bucket.
const FALLBACK_SOH_BUCKETS = [
  { label: ">98",   min: 98, color: "#1B7A42" },
  { label: "95-98", min: 95, color: "#4CAF72" },
  { label: "90-95", min: 90, color: "#7EC862" },
  { label: "85-90", min: 85, color: "#B4D95A" },
  { label: "80-85", min: 80, color: "#F5C430" },
  { label: "75-80", min: 75, color: "#E87040" },
  { label: "70-75", min: 70, color: "#D9502F" },
  { label: "<70",   min:  0, color: "#C62828" },
];

/**
 * Pure builder behind useSOHBuckets (exported for tests).
 * Range buckets are sorted best (highest SOH) -> worst; when includeUnavailable is set
 * the "SOH Unavailable" bucket is appended last.
 */
export function buildSOHBuckets(orgMetaBuckets, { includeUnavailable = true } = {}) {
  const { rangeEntries, unavailableEntry } = splitUnavailableEntry(orgMetaBuckets, SOH_UNAVAILABLE_LABEL);
  // Malformed entries are already dropped (isUsableRangeEntry); fallback when none is usable.
  const ranges = parseRangeBuckets(rangeEntries, FALLBACK_SOH_BUCKETS);
  return includeUnavailable
    ? appendUnavailableBucket(ranges, SOH_UNAVAILABLE_LABEL, unavailableEntry)
    : ranges;
}

/**
 * Returns SOH bucket config derived from org meta.
 * Each entry is { name, displayLabel, color, min } sorted best (highest SOH) -> worst,
 * followed by the "SOH Unavailable" bucket ({ ..., isUnavailable: true }).
 * `name` is the API value (filters, groupBy labels); `displayLabel` is what the UI shows.
 *
 * Reads from: BatteryIntelligence.stateOfHealthBuckets
 * Expected org meta entry shape: { "label": "95-98", "range": { "min": 95, "max": 98 }, "color": "#4CAF72" }
 * An entry with "includeNull": true sets the unavailable bucket's display label and colour.
 *
 * Pass { includeUnavailable: false } where the backend has no unavailable bucket
 * (the string-level "String SOH" filter).
 */
export function useSOHBuckets({ includeUnavailable = true } = {}) {
  const pagesContent   = usePagesContent();
  const orgMetaBuckets = pagesContent?.metaInfo?.BatteryIntelligence?.stateOfHealthBuckets;
  return useMemo(
    () => buildSOHBuckets(orgMetaBuckets, { includeUnavailable }),
    [orgMetaBuckets, includeUnavailable]
  );
}

/**
 * Classifies a numeric SOH value into a bucket name.
 * Null / NaN / negative values map to the unavailable bucket when the list has one.
 * @param {number} value   - SOH percentage value
 * @param {Array}  buckets - Bucket array from useSOHBuckets()
 * @returns {string|null} bucket name, or null if unclassifiable
 */
export function classifySOHValue(value, buckets) {
  return classifyBucketValue(value, buckets);
}

export const SOH_BUCKET_META_KEY = "StateOfHealthBucket";

/**
 * Resolves a battery row's SOH bucket name from its response metaInfo:
 * metaInfo.StateOfHealthBucket when the field is present (a null there means the server
 * left the value unclassified -> no range bucket), else classification of the SOH value.
 * See resolveBucketName.
 * @returns {string|null}
 */
export function resolveSOHCategory(metaInfo, sohValue, buckets) {
  return resolveBucketName(metaInfo, SOH_BUCKET_META_KEY, sohValue, buckets);
}
